import { Clock, Target, TrendingUp } from "lucide-react";
import { money } from "../../lib/finance";
import { Private } from "../../components/Privacy";
import type { useTripStats } from "../../hooks/use-trip-stats";
import TripAccountsSpendBar from "./TripAccountsSpendBar";

export interface TripHeroCardProps {
  stats: NonNullable<ReturnType<typeof useTripStats>>;
  budget: number | null;
  budgetCurrency: string;
  hidden: boolean;
  tripName?: string;
}

export default function TripHeroCard({
  stats,
  budget,
  budgetCurrency,
  hidden,
  tripName,
}: TripHeroCardProps) {
  const {
    totalSpendBudget,
    totalIncomeBudget,
    remainingBudget,
    budgetPercent,
    isOverBudget,
    pacing,
  } = stats;

  return (
    <div className="trip-hero-card">
      <div className="card-topline">
        <span><span className="status-dot" />TOTAL TRIP SPEND</span>
        <div className="trip-hero-tags">
          {totalIncomeBudget > 0 && (
            <span className="subtle-tag income">
              +<Private>{money(totalIncomeBudget, budgetCurrency)}</Private> funded
            </span>
          )}
          {budget !== null && (
            <span className={`subtle-tag ${isOverBudget ? "over" : ""}`}>
              {remainingBudget !== null && (
                isOverBudget
                  ? <><Private>{money(Math.abs(remainingBudget), budgetCurrency)}</Private> over</>
                  : <><Private>{money(remainingBudget, budgetCurrency)}</Private> left</>
              )}
            </span>
          )}
        </div>
      </div>

      <div className="spending-number">
        <Private>{money(totalSpendBudget, budgetCurrency).replace(` ${budgetCurrency}`, "")}</Private>
        <span>{budgetCurrency}</span>
      </div>

      {budget !== null && !hidden && (
        <div className="trip-hero-budget-progress">
          <div className="trip-hero-progress-labels">
            <span>Budget: <strong><Private>{money(budget, budgetCurrency)}</Private></strong></span>
            <span><strong>{budgetPercent}%</strong> used</span>
          </div>
          <div className={`budget-bar-track ${isOverBudget ? "over" : ""}`}>
            <div
              className="budget-bar-fill"
              style={{ width: `${Math.min(100, budgetPercent)}%` }}
            />
          </div>
        </div>
      )}

      {/* Daily Pacing Section */}
      {pacing && (() => {
        const hasBudgetRate =
          (pacing.stage === "active" && pacing.dailyBudgetRemaining !== null) ||
          (pacing.stage !== "active" && pacing.plannedDailyBudget !== null);

        return (
          <div className={`trip-pacing-grid ${hasBudgetRate ? "has-budget" : ""}`}>
            <div className="trip-pacing-item trip-pacing-timeframe">
              <span className="pacing-label">
                <Clock size={12} />
                {pacing.stage === "active"
                  ? `Day ${pacing.currentDay} of ${pacing.totalDays}`
                  : pacing.stage === "upcoming"
                    ? `In ${pacing.daysUntilStart} day${pacing.daysUntilStart === 1 ? "" : "s"}`
                    : `${pacing.totalDays} days`}
              </span>
              <span className="pacing-value">
                {pacing.stage === "active"
                  ? `${pacing.daysRemaining} day${pacing.daysRemaining === 1 ? "" : "s"} left`
                  : pacing.stage === "upcoming"
                    ? `${pacing.totalDays}d planned`
                    : "Trip ended"}
              </span>
            </div>

            <div className="trip-pacing-item">
              <span className="pacing-label">
                <TrendingUp size={12} />
                Daily average
              </span>
              <span className={`pacing-value ${pacing.isPacingOver ? "pacing-warn" : ""}`}>
                <strong><Private>{money(Math.round(pacing.dailyAverageSpend), budgetCurrency)}</Private></strong>
                <small>/day</small>
              </span>
            </div>

            {pacing.dailyBudgetRemaining !== null && pacing.stage === "active" && (
              <div className="trip-pacing-item">
                <span className="pacing-label">
                  <Target size={12} />
                  Daily target
                </span>
                <span className={`pacing-value ${pacing.isPacingOver ? "pacing-warn" : ""}`}>
                  <strong><Private>{money(Math.round(pacing.dailyBudgetRemaining), budgetCurrency)}</Private></strong>
                  <small>/day</small>
                </span>
              </div>
            )}

            {pacing.plannedDailyBudget !== null && pacing.stage !== "active" && (
              <div className="trip-pacing-item">
                <span className="pacing-label">
                  <Target size={12} />
                  Daily budget
                </span>
                <span className="pacing-value">
                  <strong><Private>{money(Math.round(pacing.plannedDailyBudget), budgetCurrency)}</Private></strong>
                  <small>/day</small>
                </span>
              </div>
            )}
          </div>
        );
      })()}

      {!hidden && stats.walletBalances.length > 0 && (
        <TripAccountsSpendBar
          walletBalances={stats.walletBalances}
          tripName={tripName}
        />
      )}
    </div>
  );
}
